import { clampRange } from '../util/math.js';
import { playHash, stopReasonForPlay, visualYardsForPlay } from './helpers.js';

export type PresentationPlanMode = 'standard' | 'extended';

export interface PresentationPlan {
  version: string;
  signature: string;
  mode: PresentationPlanMode;
  visualProfile: any;
  pressureTiming?: string;
  rallyStyle?: string;
  tackleEnvironment?: string;
  coverageShell?: string;
  pass?: any;
  run?: any;
}

const PRESENTATION_PLAN_VERSION = 'presentation-plan-v2';

function hash01(play: any, salt: string): number {
  return (playHash(play, salt) % 1000) / 999;
}

function signed(play: any, salt: string, amplitude: number): number {
  return (hash01(play, salt) * 2 - 1) * amplitude;
}

function pick<T>(play: any, salt: string, items: T[]): T {
  return items[playHash(play, salt) % items.length];
}

function intBetween(play: any, salt: string, min: number, max: number): number {
  return min + (playHash(play, salt) % Math.max(1, max - min + 1));
}

function roundTo(value: number, digits: number = 2): number {
  const precision = 10 ** digits;
  return Math.round(value * precision) / precision;
}

function playSide(context: any): 'top' | 'middle' | 'bottom' {
  const laneY = Number(context?.laneY);
  const centerY = Number(context?.centerY);
  if (!Number.isFinite(laneY) || !Number.isFinite(centerY)) {
    return 'middle';
  }
  if (laneY < centerY - 28) return 'top';
  if (laneY > centerY + 28) return 'bottom';
  return 'middle';
}

function mergeSignatures(parts: any[]): string {
  return ([] as any[]).concat(...parts)
    .filter((part) => part !== undefined && part !== null && part !== '')
    .join(':');
}

function baseTargetRole(play: any): string {
  return play?.participants?.targetRole
    || play?.targetRole
    || (Array.isArray(play?.readOrder) ? play.readOrder[0] : null)
    || 'WR1';
}

function baseRunnerRole(play: any): string {
  if (play?.qbRun || play?.playId === 'kneel') return 'QB';
  if (play?.participants?.runnerRole === 'FB') return 'FB';
  if (play?.participants?.runnerRole === 'WR') return 'SLOT';
  return 'RB';
}

function resolvePassBucket(play: any, context: any): string {
  const stopReason = stopReasonForPlay(play);
  const resultText = String(play?.result || '').toLowerCase();
  const airYards = Math.max(0, Number(play?.airYards) || 0);
  const yacYards = Math.max(0, Number(play?.yacYards) || 0);
  const visualYards = Math.max(0, visualYardsForPlay(play));
  const passFamily = String(play?.passFamily || '');
  const side = playSide(context);
  const targetRole = baseTargetRole(play);
  const deepConcept = ['play-action-post', 'flood-shot', 'hail-mary'].includes(play?.playId)
    || ['shot', 'dagger', 'hail-mary'].includes(passFamily);
  const pressureLook = String(play?.pressureLook || '');
  const shell = String(play?.coverageShell || '');

  if (resultText.includes('sack')) return 'sack';
  if (play?.scramble) return 'scramble';
  if (play?.turnover || resultText.includes('interception')) return 'turnover';
  if (stopReason === 'incomplete') return airYards >= 14 ? 'deep_incomplete' : 'quick_incomplete';
  if ((targetRole === 'RB' || targetRole === 'TE') && (airYards <= 6 || yacYards > 0)) return 'checkdown_yac';
  if (airYards <= 4 && yacYards <= 2) return 'quick_immediate';
  if (deepConcept && (pressureLook === 'mug' || pressureLook === 'edge' || play?.boxCount >= 8 || play?.playId === 'hail-mary')) return 'contested';
  if (deepConcept && (airYards >= 14 || visualYards >= 20) && (shell === 'two-high' || shell === 'quarters')) return 'layered_open';
  if (side !== 'middle' && (airYards >= 8 || visualYards >= 9)) return 'sideline_angle';
  if (airYards >= 12 || (visualYards >= 18 && yacYards >= 4)) return 'stride_pursuit';
  if (airYards >= 6) return 'inside_rally';
  return 'settled_hitch';
}

function resolveRunBucket(play: any, context: any): string {
  const stopReason = stopReasonForPlay(play);
  const gainedYards = (play?.endYardLine ?? play?.startYardLine ?? 0) - (play?.startYardLine ?? 0);
  const negativeRun = play?.playId !== 'kneel' && gainedYards < 0;
  const runFamily = String(play?.runFamily || '');
  const playId = String(play?.playId || '');
  const side = playSide(context);
  const outsideFamily = runFamily === 'toss' || runFamily === 'option' || runFamily === 'boot'
    || playId === 'toss-sweep'
    || playId === 'boot-keeper'
    || playId === 'speed-option';
  const safetyDepth = Math.max(0, Number(play?.runTacklerDepthYards) || 0);

  if (stopReason === 'kneel') return 'kneel';
  if (negativeRun) return 'interior_squeeze';
  if (stopReason === 'touchdown' || (gainedYards >= 16 && side !== 'middle')) return 'sideline_finish';
  if (safetyDepth >= 8 || gainedYards >= 14) return 'safety_cleanup';
  if (playId === 'counter-tray' || runFamily === 'counter') return gainedYards >= 5 ? 'cutback' : 'glancing_contact';
  if (outsideFamily) return gainedYards >= 4 ? 'bounce_outside' : 'frontside_crease';
  if (gainedYards >= 9) return 'second_level_fill';
  if (playId === 'power-o' || runFamily === 'power' || runFamily === 'duo') return gainedYards >= 5 ? 'frontside_crease' : 'glancing_contact';
  if (playId === 'inside-zone' || runFamily === 'zone' || runFamily === 'draw') return gainedYards >= 4 ? 'frontside_crease' : 'interior_squeeze';
  return 'glancing_contact';
}

function buildPassPlan(play: any, context: any, mode: PresentationPlanMode): any {
  const bucket = resolvePassBucket(play, context);
  const side = playSide(context);
  const targetRole = baseTargetRole(play);
  const shell = String(play?.coverageShell || '');
  const visualYards = Math.max(0, visualYardsForPlay(play));
  const throwArcBase = bucket === 'layered_open'
    ? 72
    : bucket === 'stride_pursuit' || bucket === 'contested'
      ? 64
      : bucket === 'sideline_angle'
        ? 60
        : bucket === 'checkdown_yac'
          ? 44
          : 50;
  const routeStyleByBucket: Record<string, string> = {
    quick_immediate: 'quick-break',
    settled_hitch: 'settled-breaker',
    inside_rally: 'inside-window',
    sideline_angle: 'sideline-window',
    checkdown_yac: 'checkdown',
    stride_pursuit: 'stride-catch',
    contested: 'contested',
    layered_open: 'layered-open',
    quick_incomplete: 'quick-break',
    deep_incomplete: 'layered-open',
    turnover: 'stride-catch',
    scramble: 'inside-window',
    sack: 'quick-break',
  };
  const routeStyle = routeStyleByBucket[bucket] || 'settled-breaker';
  const tackleSide = pick(play, 'pass-tackle-side', side === 'middle'
    ? ['top-shoulder', 'bottom-shoulder', 'inside']
    : side === 'top'
      ? ['bottom-shoulder', 'inside', 'top-shoulder']
      : ['top-shoulder', 'inside', 'bottom-shoulder']);
  const routeStemScale = bucket === 'quick_immediate'
    ? 0.9
    : bucket === 'layered_open' || bucket === 'stride_pursuit'
      ? 1.08
      : bucket === 'contested'
        ? 1.02
        : 1;
  const spacingScale = bucket === 'contested'
    ? 0.9
    : bucket === 'layered_open' || mode === 'extended'
      ? 1.08
      : 1;
  const breakLean = bucket === 'quick_immediate'
    ? 0.32
    : bucket === 'settled_hitch'
      ? 0.48
      : bucket === 'sideline_angle'
        ? 0.56
        : bucket === 'layered_open'
          ? 0.62
          : 0.44;
  const catchStrideScale = bucket === 'stride_pursuit' || bucket === 'layered_open'
    ? 1.12
    : bucket === 'checkdown_yac'
      ? 0.92
      : 1;
  const pursuitSpread = bucket === 'contested'
    ? 0.88
    : bucket === 'layered_open'
      ? 1.16
      : bucket === 'stride_pursuit'
        ? 1.08
        : 1;
  const arrivalJitterFrames = intBetween(play, 'pass-arrival-jitter', -1, mode === 'extended' ? 3 : 2);
  const qbHitchFrames = bucket === 'quick_immediate'
    ? intBetween(play, 'pass-hitch-quick', 0, 1)
    : bucket === 'layered_open' || bucket === 'stride_pursuit'
      ? intBetween(play, 'pass-hitch-deep', 1, 3)
      : intBetween(play, 'pass-hitch-base', 0, 2);
  const releaseDelayFrames = bucket === 'contested'
    ? intBetween(play, 'pass-release-contested', 1, 3)
    : bucket === 'checkdown_yac'
      ? intBetween(play, 'pass-release-checkdown', 0, 1)
      : intBetween(play, 'pass-release-base', -1, 2);
  const catchOffsetY = bucket === 'sideline_angle'
    ? signed(play, 'pass-catch-wide', 10)
    : bucket === 'inside_rally'
      ? signed(play, 'pass-catch-middle', 6)
      : signed(play, 'pass-catch-base', 4);
  const catchOffsetX = bucket === 'layered_open'
    ? intBetween(play, 'pass-catch-x-deep', 2, 8)
    : bucket === 'quick_immediate'
      ? intBetween(play, 'pass-catch-x-quick', 0, 3)
      : intBetween(play, 'pass-catch-x-base', 1, 5);
  const plan = {
    bucket,
    targetRole,
    routeStyle,
    catchStyle: bucket === 'contested' ? 'high-point' : bucket === 'stride_pursuit' ? 'in-stride' : bucket === 'settled_hitch' ? 'settled' : 'gather',
    routeStemScale: roundTo(clampRange(routeStemScale + signed(play, 'pass-stem-scale', 0.08), 0.84, 1.18)),
    routeSpacingScale: roundTo(clampRange(spacingScale + signed(play, 'pass-spacing-scale', 0.1), 0.84, 1.2)),
    breakLean: roundTo(clampRange(breakLean + signed(play, 'pass-break-lean', 0.08), 0.28, 0.68)),
    breakYOffset: Math.round(signed(play, 'pass-break-y', bucket === 'sideline_angle' ? 14 : 10)),
    laneBend: Math.round(signed(play, 'pass-lane-bend', bucket === 'layered_open' || mode === 'extended' ? 18 : 10)),
    catchOffsetX,
    catchOffsetY: Math.round(clampRange(catchOffsetY, -14, 14)),
    catchStrideScale: roundTo(clampRange(catchStrideScale + signed(play, 'pass-catch-stride', 0.08), 0.84, 1.18)),
    throwArcHeight: Math.round(clampRange(throwArcBase + signed(play, 'pass-arc', bucket === 'layered_open' ? 8 : 6), 38, 82)),
    throwLeadX: intBetween(play, 'pass-throw-lead-x', bucket === 'checkdown_yac' ? 2 : 4, bucket === 'layered_open' ? 11 : 8),
    throwLeadY: Math.round(signed(play, 'pass-throw-lead-y', bucket === 'sideline_angle' ? 6 : 4)),
    qbHitchFrames,
    releaseDelayFrames,
    qbDepthOffset: intBetween(play, 'pass-qb-depth', bucket === 'layered_open' ? 2 : 0, bucket === 'layered_open' || bucket === 'stride_pursuit' ? 7 : 4),
    qbOffsetY: Math.round(signed(play, 'pass-qb-y', 5)),
    pursuitSpread: roundTo(clampRange(pursuitSpread + signed(play, 'pass-pursuit-spread', 0.08), 0.84, 1.18)),
    arrivalJitterFrames,
    tackleSide,
    tackleFrameBias: intBetween(play, 'pass-tackle-frame', mode === 'extended' ? 0 : -1, mode === 'extended' ? 4 : 2),
    fallForwardScale: roundTo(clampRange((bucket === 'contested' ? 0.92 : bucket === 'stride_pursuit' ? 1.12 : 1) + signed(play, 'pass-fall-forward', 0.08), 0.84, 1.2)),
    contactIntensity: roundTo(clampRange((bucket === 'contested' ? 1.12 : bucket === 'quick_immediate' ? 0.9 : 1) + signed(play, 'pass-contact', 0.08), 0.84, 1.18)),
    heroPursuitCount: mode === 'extended' ? 4 : 3,
    backgroundDepthScale: roundTo(clampRange((mode === 'extended' ? 0.82 : 0.9) + signed(play, 'pass-background-depth', 0.06), 0.72, 0.98)),
    coverageScale: roundTo(clampRange((shell === 'two-high' || shell === 'quarters' ? 0.96 : 1.02) + signed(play, 'pass-coverage-scale', 0.04), 0.9, 1.08)),
    extendedWindowFrames: mode === 'extended' ? intBetween(play, 'pass-extended-window', 4, 9) : 0,
  };

  const pressureTiming = bucket === 'quick_immediate' || bucket === 'contested'
    ? 'trigger'
    : bucket === 'layered_open' || bucket === 'sideline_angle' || bucket === 'checkdown_yac'
      ? 'delayed'
      : 'balanced';
  const rallyStyle = bucket === 'contested'
    ? 'swarm'
    : bucket === 'layered_open' || bucket === 'sideline_angle'
      ? 'vice'
      : bucket === 'stride_pursuit'
        ? 'inside-out'
        : bucket === 'checkdown_yac'
          ? 'downhill'
          : 'inside-out';
  const tackleEnvironment = bucket === 'contested'
    ? 'crowded'
    : bucket === 'layered_open' || bucket === 'stride_pursuit'
      ? 'space'
      : visualYards >= 16
        ? 'leverage'
        : 'crowded';

  return {
    ...plan,
    pressureTiming,
    rallyStyle,
    tackleEnvironment,
    coverageShell: bucket === 'layered_open' ? 'two-high' : bucket === 'contested' ? 'robber' : undefined,
  };
}

function buildRunPlan(play: any, context: any, mode: PresentationPlanMode): any {
  const bucket = resolveRunBucket(play, context);
  const side = playSide(context);
  const runnerRole = baseRunnerRole(play);
  const gainedYards = (play?.endYardLine ?? play?.startYardLine ?? 0) - (play?.startYardLine ?? 0);
  const runStyleByBucket: Record<string, string> = {
    kneel: 'surge',
    frontside_crease: 'crease',
    interior_squeeze: 'squeeze',
    bounce_outside: 'bounce',
    cutback: 'cutback',
    second_level_fill: 'burst-lane',
    glancing_contact: 'glance',
    safety_cleanup: 'alley',
    sideline_finish: 'sideline',
  };
  const runStyle = runStyleByBucket[bucket] || 'crease';
  const tackleSide = pick(play, 'run-tackle-side', side === 'middle'
    ? ['inside', 'top-hip', 'bottom-hip']
    : side === 'top'
      ? ['bottom-hip', 'inside', 'top-hip']
      : ['top-hip', 'inside', 'bottom-hip']);
  const leadRole = bucket === 'bounce_outside' || bucket === 'sideline_finish'
    ? pick(play, 'run-lead-outside', ['TE', 'SLOT', 'WR2'])
    : bucket === 'cutback'
      ? pick(play, 'run-lead-cutback', ['TE', 'WR1', 'SLOT'])
      : pick(play, 'run-lead-inside', ['TE', 'FB', 'SLOT']);
  const laneBend = bucket === 'bounce_outside' || bucket === 'sideline_finish'
    ? signed(play, 'run-lane-bend-wide', 18)
    : bucket === 'cutback'
      ? signed(play, 'run-lane-bend-cutback', 12)
      : signed(play, 'run-lane-bend-base', 8);
  const surfaceStretch = bucket === 'bounce_outside' || bucket === 'sideline_finish'
    ? 1.12
    : bucket === 'interior_squeeze'
      ? 0.9
      : bucket === 'cutback'
        ? 1.06
        : 1;
  const pressPatience = bucket === 'cutback'
    ? 1.14
    : bucket === 'interior_squeeze'
      ? 0.88
      : bucket === 'glancing_contact'
        ? 0.92
        : 1;
  const contactIntensity = bucket === 'glancing_contact'
    ? 0.88
    : bucket === 'interior_squeeze'
      ? 1.12
      : bucket === 'sideline_finish'
        ? 0.94
        : 1;
  const pursuitSpread = bucket === 'safety_cleanup'
    ? 1.14
    : bucket === 'sideline_finish'
      ? 1.08
      : bucket === 'interior_squeeze'
        ? 0.9
        : 1;
  const fallForwardDistance = bucket === 'glancing_contact'
    ? intBetween(play, 'run-fall-forward-glance', 2, 5)
    : bucket === 'interior_squeeze'
      ? intBetween(play, 'run-fall-forward-squeeze', 0, 2)
      : bucket === 'frontside_crease'
        ? intBetween(play, 'run-fall-forward-crease', 1, 4)
        : intBetween(play, 'run-fall-forward-base', 1, 3);
  const openFieldTriggerProgress = bucket === 'safety_cleanup' || bucket === 'sideline_finish'
    ? 0.42
    : bucket === 'second_level_fill' || bucket === 'bounce_outside' || bucket === 'cutback'
      ? 0.46
      : 0.52;
  const openFieldTriggerYards = bucket === 'safety_cleanup' || bucket === 'sideline_finish'
    ? intBetween(play, 'run-open-field-yards-deep', 5, 8)
    : bucket === 'second_level_fill' || bucket === 'bounce_outside' || bucket === 'cutback'
      ? intBetween(play, 'run-open-field-yards-wide', 4, 7)
      : intBetween(play, 'run-open-field-yards-base', 4, 6);
  const trenchFadeScale = mode === 'extended'
    ? 0.34
    : bucket === 'interior_squeeze'
      ? 0.48
      : 0.42;
  const backgroundTrailDistance = mode === 'extended'
    ? intBetween(play, 'run-trail-distance-extended', 28, 42)
    : intBetween(play, 'run-trail-distance-base', 20, 32);
  const pursuitFocusBoost = bucket === 'safety_cleanup'
    ? 1.18
    : bucket === 'sideline_finish'
      ? 1.14
      : bucket === 'second_level_fill'
        ? 1.1
        : 1.04;
  const finalApproachDistance = bucket === 'safety_cleanup'
    ? intBetween(play, 'run-final-approach-safety', 12, 15)
    : bucket === 'sideline_finish'
      ? intBetween(play, 'run-final-approach-sideline', 11, 14)
      : bucket === 'glancing_contact' || bucket === 'interior_squeeze'
        ? intBetween(play, 'run-final-approach-tight', 8, 11)
        : intBetween(play, 'run-final-approach-base', 9, 13);
  const finisherBufferFrames = mode === 'extended'
    ? intBetween(play, 'run-finisher-buffer-extended', 6, 10)
    : intBetween(play, 'run-finisher-buffer-base', 4, 7);

  const plan = {
    bucket,
    runnerRole,
    runStyle,
    laneBend: Math.round(clampRange(laneBend, -20, 20)),
    surfaceStretch: roundTo(clampRange(surfaceStretch + signed(play, 'run-surface-stretch', 0.08), 0.84, 1.18)),
    pressPatience: roundTo(clampRange(pressPatience + signed(play, 'run-press-patience', 0.08), 0.84, 1.18)),
    pressYOffset: Math.round(signed(play, 'run-press-y', bucket === 'bounce_outside' ? 12 : 8)),
    cutbackAmount: roundTo(clampRange((bucket === 'cutback' ? 1.16 : bucket === 'bounce_outside' ? 0.92 : 1) + signed(play, 'run-cutback-amount', 0.08), 0.86, 1.22)),
    pursuitSpread: roundTo(clampRange(pursuitSpread + signed(play, 'run-pursuit-spread', 0.08), 0.84, 1.18)),
    arrivalJitterFrames: intBetween(play, 'run-arrival-jitter', -1, mode === 'extended' ? 3 : 2),
    tackleSide,
    tackleFrameBias: intBetween(play, 'run-tackle-frame', mode === 'extended' ? 0 : -1, mode === 'extended' ? 4 : 2),
    fallForwardDistance,
    contactIntensity: roundTo(clampRange(contactIntensity + signed(play, 'run-contact-intensity', 0.08), 0.84, 1.18)),
    leadRole,
    heroPursuitCount: mode === 'extended' ? 4 : 3,
    backgroundDriftScale: roundTo(clampRange((mode === 'extended' ? 0.52 : 0.62) + signed(play, 'run-background-drift', 0.06), 0.42, 0.7)),
    openFieldTriggerProgress: roundTo(clampRange(openFieldTriggerProgress + signed(play, 'run-open-field-progress', 0.04), 0.32, 0.64)),
    openFieldTriggerYards,
    trenchFadeScale: roundTo(clampRange(trenchFadeScale + signed(play, 'run-trench-fade', 0.04), 0.22, 0.56)),
    backgroundTrailDistance,
    pursuitFocusBoost: roundTo(clampRange(pursuitFocusBoost + signed(play, 'run-pursuit-focus', 0.05), 0.96, 1.24)),
    finalApproachDistance,
    finisherBufferFrames,
    extendedWindowFrames: mode === 'extended' ? intBetween(play, 'run-extended-window', 5, 10) : 0,
  };

  const pressureTiming = bucket === 'interior_squeeze'
    ? 'trigger'
    : bucket === 'sideline_finish' || bucket === 'safety_cleanup'
      ? 'delayed'
      : 'balanced';
  const rallyStyle = bucket === 'interior_squeeze'
    ? 'downhill'
    : bucket === 'safety_cleanup' || bucket === 'sideline_finish'
      ? 'vice'
      : bucket === 'glancing_contact'
        ? 'swarm'
        : 'inside-out';
  const tackleEnvironment = bucket === 'interior_squeeze'
    ? 'crowded'
    : bucket === 'safety_cleanup' || bucket === 'sideline_finish'
      ? 'space'
      : gainedYards >= 8
        ? 'leverage'
        : 'crowded';

  return {
    ...plan,
    pressureTiming,
    rallyStyle,
    tackleEnvironment,
  };
}

function resolveMode(play: any): PresentationPlanMode {
  const visualYards = Math.max(0, visualYardsForPlay(play));
  const airYards = Math.max(0, Number(play?.airYards) || 0);
  const yacYards = Math.max(0, Number(play?.yacYards) || 0);
  const runTacklerDepth = Math.max(0, Number(play?.runTacklerDepthYards) || 0);
  if (play?.family === 'pass') {
    return airYards >= 16 || visualYards >= 20 || yacYards >= 8 || play?.playId === 'hail-mary'
      ? 'extended'
      : 'standard';
  }
  if (play?.family === 'run') {
    return visualYards >= 14 || runTacklerDepth >= 8 || stopReasonForPlay(play) === 'touchdown'
      ? 'extended'
      : 'standard';
  }
  return 'standard';
}

export function buildPresentationPlan(play: any, context: any = {}): PresentationPlan {
  const mode = resolveMode(play);
  const pass = play?.family === 'pass' ? buildPassPlan(play, context, mode) : null;
  const run = play?.family === 'run' ? buildRunPlan(play, context, mode) : null;
  const visualProfile = {
    routeStyle: pass?.routeStyle,
    runStyle: run?.runStyle,
    stemDepthScale: pass?.routeStemScale,
    routeSpacingScale: pass?.routeSpacingScale,
    breakLean: pass?.breakLean,
    breakYOffset: pass?.breakYOffset,
    laneBend: play?.family === 'pass' ? pass?.laneBend : run?.laneBend,
    catchStyle: pass?.catchStyle,
    catchStrideScale: pass?.catchStrideScale,
    catchOffsetX: pass?.catchOffsetX,
    catchOffsetY: pass?.catchOffsetY,
    throwArcHeight: pass?.throwArcHeight,
    surfaceStretch: run?.surfaceStretch,
    pressPatience: run?.pressPatience,
    pressYOffset: run?.pressYOffset,
    cutbackAmount: run?.cutbackAmount,
    contactIntensity: pass?.contactIntensity ?? run?.contactIntensity,
    fallForwardScale: pass?.fallForwardScale,
    fallForwardDistance: run?.fallForwardDistance,
    tackleSide: pass?.tackleSide ?? run?.tackleSide,
    pursuitSpread: pass?.pursuitSpread ?? run?.pursuitSpread,
    backgroundDepthScale: pass?.backgroundDepthScale,
    backgroundDriftScale: run?.backgroundDriftScale,
    extendedMode: mode === 'extended',
    extendedWindowFrames: pass?.extendedWindowFrames ?? run?.extendedWindowFrames ?? 0,
    presentationBucket: pass?.bucket ?? run?.bucket ?? 'default',
  };
  const signature = mergeSignatures([
    PRESENTATION_PLAN_VERSION,
    mode,
    pass?.bucket || '',
    run?.bucket || '',
    visualProfile.routeStyle || '',
    visualProfile.runStyle || '',
    visualProfile.stemDepthScale || '',
    visualProfile.breakYOffset || '',
    visualProfile.laneBend || '',
    visualProfile.catchStrideScale || '',
    visualProfile.throwArcHeight || '',
    visualProfile.surfaceStretch || '',
    visualProfile.pressPatience || '',
    visualProfile.contactIntensity || '',
    visualProfile.tackleSide || '',
    run?.openFieldTriggerProgress || '',
    run?.trenchFadeScale || '',
    run?.finalApproachDistance || '',
  ]);

  return {
    version: PRESENTATION_PLAN_VERSION,
    signature,
    mode,
    visualProfile,
    pressureTiming: pass?.pressureTiming ?? run?.pressureTiming,
    rallyStyle: pass?.rallyStyle ?? run?.rallyStyle,
    tackleEnvironment: pass?.tackleEnvironment ?? run?.tackleEnvironment,
    coverageShell: pass?.coverageShell,
    pass,
    run,
  };
}

export function applyPresentationPlan(play: any, plan: PresentationPlan): any {
  const arcadePresentation = play?.arcadeReplay?.source === 'arcade-sim-v1';
  const explicitVisualProfile = play?.visualProfile || {};
  return {
    ...play,
    pressureTiming: arcadePresentation ? plan.pressureTiming : (play?.pressureTiming || plan.pressureTiming),
    rallyStyle: arcadePresentation ? plan.rallyStyle : (play?.rallyStyle || plan.rallyStyle),
    tackleEnvironment: arcadePresentation ? plan.tackleEnvironment : (play?.tackleEnvironment || plan.tackleEnvironment),
    coverageShell: arcadePresentation ? (plan.coverageShell || play?.coverageShell) : (play?.coverageShell || plan.coverageShell),
    visualProfile: {
      ...(arcadePresentation ? explicitVisualProfile : plan.visualProfile),
      ...plan.visualProfile,
      ...(arcadePresentation ? {} : explicitVisualProfile),
    },
  };
}
